import { collection, addDoc, serverTimestamp, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Sample news articles data (expanded from the current mock data)
const newsArticles = [
  {
    id: 1,
    title: 'APN Q1 NEWSLETTER',
    image: '/images/news/q1-newsletter.png',
    date: 'April 15, 2022',
    author: 'Caroline Burnham',
    category: 'News',
    featured: true,
    excerpt: 'Check out our Q1 newsletter with updates on recent events and upcoming opportunities.',
    content: `
      <div class="news-content">
        <h2 class="text-2xl font-bold mb-4">WE ARE LIVE!</h2>
        <p class="mb-4">We are proud to announce that our website is now accessible to all prospective members of APN. Current features that we have rolled out include the membership and event registration pages, in addition to articles that detail updates about our members, general network announcements, and notifications about new events.</p>
        
        <h2 class="text-2xl font-bold mb-4">Join us for our first IN-PERSON event!</h2>
        <p class="mb-6">APN is excited to be hosting our first event at Printers Alley on Tuesday, April 12th from 6:30-8:30pm. Check out the Events page to register.</p>
        
        <h2 class="text-2xl font-bold mb-4">MEMBERSHIP</h2>
        <p class="mb-6">Prospective members can now register to become an official part of the Accelerated Professionals Network. Membership will be on a tiered system, so make sure to register for the correct one! All details are on the Membership page. Feel free to contact Cara with any questions.</p>
        
        <h2 class="text-2xl font-bold mb-4">Recommended Business Books</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div class="bg-gray-100 p-4 rounded-lg">
            <h3 class="font-bold mb-2">Rich Dad Poor Dad</h3>
            <p>A classic personal finance book that teaches important lessons about money and investing.</p>
          </div>
          <div class="bg-gray-100 p-4 rounded-lg">
            <h3 class="font-bold mb-2">How to Win Friends and Influence People</h3>
            <p>Dale Carnegie's timeless advice on building relationships and effective communication.</p>
          </div>
        </div>
      </div>
    `,
    featured: true,
    status: 'published'
  },
  {
    id: 2,
    title: 'APN Network Growth Update',
    image: '/images/news/network-growth.png',
    date: 'June 20, 2022',
    author: 'APN Team',
    category: 'News',
    excerpt: 'Our network continues to grow with new members joining from top financial institutions.',
    content: `
      <div class="news-content">
        <h2 class="text-2xl font-bold mb-4">Network Expansion</h2>
        <p class="mb-4">We're excited to share that APN has welcomed over 200 new members in the past quarter, representing professionals from leading financial institutions including Goldman Sachs, Morgan Stanley, BlackRock, and many others.</p>
        
        <h2 class="text-2xl font-bold mb-4">New Features</h2>
        <p class="mb-4">Based on member feedback, we've launched several new features including enhanced networking tools, industry-specific groups, and improved event management capabilities.</p>
        
        <h2 class="text-2xl font-bold mb-4">Upcoming Events</h2>
        <p class="mb-4">Mark your calendars for our upcoming events in Chicago, New York, and our first virtual networking session.</p>
      </div>
    `,
    featured: false,
    status: 'published'
  },
  {
    id: 3,
    title: 'Industry Insights: M&A Trends 2022',
    image: '/images/news/ma-trends.png',
    date: 'August 10, 2022',
    author: 'Michael Chen',
    category: 'Feature',
    excerpt: 'An in-depth analysis of current M&A trends and what they mean for finance professionals.',
    content: `
      <div class="news-content">
        <h2 class="text-2xl font-bold mb-4">M&A Market Overview</h2>
        <p class="mb-4">The M&A market in 2022 has shown resilience despite economic uncertainties. Deal volumes remain strong across key sectors including technology, healthcare, and financial services.</p>
        
        <h2 class="text-2xl font-bold mb-4">Key Trends</h2>
        <ul class="list-disc list-inside mb-4">
          <li>Increased focus on ESG considerations in deal structuring</li>
          <li>Rise of special purpose acquisition companies (SPACs)</li>
          <li>Cross-border transactions gaining momentum</li>
          <li>Technology-driven due diligence processes</li>
        </ul>
        
        <h2 class="text-2xl font-bold mb-4">Career Implications</h2>
        <p class="mb-4">These trends present new opportunities for finance professionals to develop specialized skills in ESG analysis, technology integration, and cross-border regulatory compliance.</p>
      </div>
    `,
    featured: true,
    status: 'published'
  },
  {
    id: 4,
    title: 'APN Chicago Event Recap',
    image: '/images/news/chicago-recap.png',
    date: 'September 15, 2022',
    author: 'Sarah Johnson',
    category: 'Events',
    excerpt: 'Highlights from our successful Chicago networking event with over 150 attendees.',
    content: `
      <div class="news-content">
        <h2 class="text-2xl font-bold mb-4">Event Success</h2>
        <p class="mb-4">Our Chicago networking event was a tremendous success, bringing together over 150 finance professionals from across the Midwest region.</p>
        
        <h2 class="text-2xl font-bold mb-4">Key Highlights</h2>
        <ul class="list-disc list-inside mb-4">
          <li>Panel discussion on "Future of Investment Banking"</li>
          <li>Networking sessions with industry leaders</li>
          <li>Career development workshops</li>
          <li>New member welcome reception</li>
        </ul>
        
        <h2 class="text-2xl font-bold mb-4">Thank You</h2>
        <p class="mb-4">Special thanks to our sponsors Goldman Sachs, BlackRock, and Citi for making this event possible.</p>
      </div>
    `,
    featured: false,
    status: 'published'
  }
];

// Helper function to convert date string to Date object
const parseArticleDate = (dateStr) => {
  try {
    return new Date(dateStr);
  } catch (error) {
    console.error('Error parsing date:', dateStr, error);
    return new Date(); // Fallback to current date
  }
};

// Function to clean and prepare article data for Firebase
const prepareArticleForFirebase = (article) => {
  const publishedAt = parseArticleDate(article.date);

  return {
    title: article.title || '',
    excerpt: article.excerpt || '',
    content: article.content || '',
    author: article.author || 'APN Team',
    category: article.category || 'News',
    image: article.image || '/images/news/default-news.png',
    featured: article.featured || false,
    status: article.status || 'published',
    
    // Engagement metrics
    views: 0,
    likes: 0,
    comments: [],
    
    // SEO and metadata
    slug: article.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
    tags: article.tags || [],
    metaDescription: article.excerpt || '',
    
    // Dates
    publishedAt: publishedAt,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    
    // Original ID for reference
    originalId: article.id
  };
};

// Function to clear existing articles (for clean migration)
export const clearExistingArticles = async () => {
  try {
    console.log('🗑️ Clearing existing articles...');
    const articlesRef = collection(db, 'articles');
    const snapshot = await getDocs(articlesRef);
    
    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    console.log(`✅ Cleared ${snapshot.size} existing articles`);
    return snapshot.size;
  } catch (error) {
    console.error('❌ Error clearing articles:', error);
    throw error;
  }
};

// Function to migrate news articles
export const migrateNewsArticles = async () => {
  try {
    console.log('📰 Migrating news articles...');
    const articlesRef = collection(db, 'articles');
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const article of newsArticles) {
      try {
        const articleData = prepareArticleForFirebase(article);
        await addDoc(articlesRef, articleData);
        console.log(`✅ Migrated article: ${article.title}`);
        successCount++;
      } catch (error) {
        console.error(`❌ Error migrating article ${article.title}:`, error);
        errorCount++;
      }
    }
    
    console.log(`📊 News migration complete: ${successCount} success, ${errorCount} errors`);
    return { successCount, errorCount };
  } catch (error) {
    console.error('❌ Error migrating news articles:', error);
    throw error;
  }
};

// Main migration function
export const migrateAllNews = async (clearFirst = false) => {
  try {
    console.log('🚀 Starting news migration...');
    
    let clearedCount = 0;
    if (clearFirst) {
      clearedCount = await clearExistingArticles();
    }
    
    const results = await migrateNewsArticles();
    
    console.log('🎉 News migration complete!');
    console.log(`📊 Summary: ${results.successCount} articles migrated, ${results.errorCount} errors, ${clearedCount} cleared`);
    
    return {
      cleared: clearedCount,
      migrated: results.successCount,
      errors: results.errorCount
    };
  } catch (error) {
    console.error('❌ News migration failed:', error);
    throw error;
  }
};
